import { useState } from "react";
import { Share2, Copy, Check, Code2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import type { Location } from "@/data/locations";
import { prayerCanonical, type PrayerVariant } from "@/lib/seo/prayer-schema";
import { displayLocationTitle } from "@/lib/prayer/copy";

export function PrayerShareDialog({ variant, location }: { variant: PrayerVariant; location: Location }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const url = prayerCanonical(variant, location);
  const display = displayLocationTitle(location);
  const title = `${variant === "sholat" ? "Jadwal Sholat" : variant === "imsak" ? "Jadwal Imsakiyah" : "Jadwal Buka Puasa"} ${display}`;
  const embedPath = location.kind === "kecamatan"
    ? `/embed/jadwal-${variant}/${location.parentSlug}/${location.slug}`
    : `/embed/jadwal-${variant}/${location.slug}`;
  const embedSrc = `https://hari.co.id${embedPath}`;
  const embedCode = `<iframe src="${embedSrc}" width="320" height="420" frameborder="0" title="${title}" style="border:1px solid #e5e7eb;border-radius:12px"></iframe>`;

  function nativeShare() {
    if (typeof navigator !== "undefined" && (navigator as Navigator & { share?: (data: ShareData) => Promise<void> }).share) {
      navigator.share!({ title, url, text: title }).catch(() => {});
    } else {
      navigator.clipboard.writeText(url).then(() => toast.success("Link disalin"));
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Share2 className="mr-2 h-4 w-4" /> Bagikan
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bagikan & Embed</DialogTitle>
          <DialogDescription>Bagikan jadwal {display} atau tanam widget di situs Anda.</DialogDescription>
        </DialogHeader>

        <div>
          <p className="mb-2 text-sm font-medium">Link halaman</p>
          <div className="flex gap-2">
            <input readOnly value={url} className="flex-1 rounded-md border border-border bg-muted px-3 py-2 text-sm" />
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button size="sm" onClick={nativeShare} className="bg-prayer text-prayer-foreground hover:bg-prayer/90">
              <Share2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div>
          <p className="mb-2 flex items-center gap-2 text-sm font-medium"><Code2 className="h-4 w-4" /> Embed widget (iframe)</p>
          <textarea readOnly value={embedCode} className="h-24 w-full rounded-md border border-border bg-muted p-2 font-mono text-xs" />
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(embedCode); setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 1500); }}>
              {embedCopied ? <><Check className="mr-2 h-4 w-4" /> Tersalin</> : <><Copy className="mr-2 h-4 w-4" /> Salin kode</>}
            </Button>
            <a href={embedSrc} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent">
              Pratinjau
            </a>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Widget menampilkan jadwal hari ini (Subuh–Isya) dengan branding minimal. Cocok untuk situs masjid atau organisasi.</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
